import { z } from 'zod/v4'

import { jsonDocument } from '../../contract/index.js'
import { defineScreen, type ScreenData, type ScreenQuery } from '../define-screen.js'
import { freshnessSchema } from '../freshness.js'
import { screenSection } from '../section.js'

/*
 * «Redes blockchain»: the networks, all their RPC endpoints, the RPC provider catalogue, the enabled VM families and the
 * sweep catalogue of each family in ONE answer. Rows travel field by field and NONE carries a secret: an endpoint says
 * whether it has a key and its last four characters, exactly as the old routes do. `metadata` (free text) and the
 * timestamps nobody paints do not travel.
 */

/** The four of the CHECK `rpc_providers_auth_style_valid`. */
export const RPC_PROVIDER_AUTH_STYLES = ['none', 'url-template', 'header', 'query-param'] as const

/** Why the runtime is NOT serving an enabled endpoint (computed by the code, not stored). */
export const RPC_ENDPOINT_RUNTIME_DROP_REASONS = [
  'credential-unreadable',
  'url-unresolvable',
] as const

const rpcScalarSchema = z.union([z.string(), z.number(), z.boolean(), z.null()])
const rpcParamDocumentSchema = jsonDocument({
  reason:
    'a JSON-RPC parameter of a probe or a sweep call: an object whose keys depend on the method being called',
})
/**
 * One JSON-RPC parameter of a health probe: a scalar, a small object or a list of those (one level, which is all a
 * probe ever carries; anything deeper travels as its JSON text, see `toRpcParam`).
 */
const rpcParamSchema = z.union([
  rpcScalarSchema,
  rpcParamDocumentSchema,
  z.array(z.union([rpcScalarSchema, rpcParamDocumentSchema])),
])
/** The parameters of a probe: also what the admin routes accept, so no door admits what another refuses. */
export const blockchainRpcParamsSchema = z.array(rpcParamSchema)
const rpcParamsSchema = blockchainRpcParamsSchema
const probeExpectationSchema = jsonDocument({
  reason:
    'health_check_expectation: what the probe reply must contain, whose keys depend on the probe method',
})

export const blockchainNetworkRowSchema = z.strictObject({
  chainId: z.number(),
  slug: z.string(),
  displayName: z.string(),
  nativeToken: z.string(),
  nativeTokenDecimals: z.number(),
  blockExplorer: z.string(),
  avgBlockTimeSeconds: z.number(),
  coingeckoNativeId: z.string(),
  wrappedNativeAddress: z.string().nullable(),
  defillamaSlug: z.string().nullable(),
  enabled: z.boolean(),
  vmFamily: z.string(),
  isEvm: z.boolean(),
  /** `null` = inherits the value of its family (`vmFamilies`). */
  addressPattern: z.string().nullable(),
  txidPattern: z.string().nullable(),
  healthCheckMethod: z.string().nullable(),
  healthCheckParams: rpcParamsSchema.nullable(),
  healthCheckExpectation: probeExpectationSchema.nullable(),
  brandColor: z.string().nullable(),
  rpcEndpointCount: z.number(),
  enabledRpcEndpointCount: z.number(),
})

export const blockchainRpcEndpointRowSchema = z.strictObject({
  id: z.string(),
  chainId: z.number(),
  /** AS STORED: never carries the key (CHECK `blockchain_rpc_endpoints_url_has_no_inline_key`); may hold the literal `{{apiKey}}`. */
  url: z.string(),
  redactedUrl: z.string(),
  providerSlug: z.string(),
  apiKeyConfigured: z.boolean(),
  /** The last four characters, to recognise the stored key; the key itself never travels. */
  apiKeyLast4: z.string().nullable(),
  /** `null` = inherits those of the provider. */
  capabilities: z.array(z.string()).nullable(),
  priority: z.number(),
  timeoutMs: z.number(),
  requestsPerSecond: z.number(),
  burstLimit: z.number(),
  maxRetries: z.number(),
  enabled: z.boolean(),
  healthCheckMethod: z.string().nullable(),
  healthCheckParams: rpcParamsSchema.nullable(),
  healthCheckExpectation: probeExpectationSchema.nullable(),
  /** `'healthy'` or `'unhealthy'` (written by the probe); text because the column has no CHECK. */
  lastHealthStatus: z.string().nullable(),
  lastHealthCheckedAt: z.string().nullable(),
  lastHealthLatencyMs: z.number().nullable(),
  consecutiveFailureCount: z.number(),
  runtimeDropReason: z.enum(RPC_ENDPOINT_RUNTIME_DROP_REASONS).nullable(),
})

export const blockchainRpcProviderSchema = z.strictObject({
  slug: z.string(),
  displayName: z.string(),
  authStyle: z.enum(RPC_PROVIDER_AUTH_STYLES),
  authHeaderName: z.string().nullable(),
  authHeaderPrefix: z.string().nullable(),
  authQueryParamName: z.string().nullable(),
  urlTemplate: z.string().nullable(),
  defaultCapabilities: z.array(z.string()),
  docsUrl: z.string().nullable(),
})

export const blockchainVmFamilySchema = z.strictObject({
  slug: z.string(),
  displayName: z.string(),
  adapterKey: z.string(),
  hasImportAdapter: z.boolean(),
  defaultAddressPattern: z.string().nullable(),
  defaultTxidPattern: z.string().nullable(),
  defaultHealthCheckMethod: z.string().nullable(),
  defaultHealthCheckParams: rpcParamsSchema,
  defaultHealthCheckExpectation: probeExpectationSchema.nullable(),
})

export const blockchainSweepPhaseSchema = z.strictObject({
  slug: z.string(),
  vmFamily: z.string(),
  displayName: z.string(),
  description: z.string().nullable(),
  startOrder: z.number(),
  isTerminal: z.boolean(),
  enabled: z.boolean(),
})

export const blockchainSweepStepSchema = z.strictObject({
  id: z.string(),
  vmFamily: z.string(),
  chainId: z.number().nullable(),
  stepKey: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  phase: z.string(),
  rpcMethod: z.string(),
  paramMapping: jsonDocument({
    reason:
      'blockchain_sweep_steps.param_mapping: how each RPC parameter is built, with keys that depend on the method',
  }),
  responseConfig: jsonDocument({
    reason:
      'blockchain_sweep_steps.response_config: where the data sits in the reply, with keys that depend on the method',
  }),
  transitions: z.record(z.string(), z.string()),
  executionOrder: z.number(),
  batchSize: z.number().nullable(),
  rateWeight: z.number(),
  requiresCapability: z.string().nullable(),
  isEnabled: z.boolean(),
})

export const blockchainSweepCatalogSchema = z.strictObject({
  vmFamily: z.string(),
  phases: z.array(blockchainSweepPhaseSchema),
  steps: z.array(blockchainSweepStepSchema),
  requiresWorkerRestart: z.boolean(),
})

/** No filters: the expanded row and the chosen family are the state of the page, and their data already travels. */
export const adminBlockchainNetworksScreen = defineScreen({
  id: 'admin.blockchainNetworks',
  path: '/api/screens/admin/blockchain-networks',
  access: 'super_admin',
  query: z.strictObject({}),
  response: z.strictObject({
    networks: screenSection(z.array(blockchainNetworkRowSchema)),
    rpcEndpoints: screenSection(z.array(blockchainRpcEndpointRowSchema)),
    rpcProviders: screenSection(z.array(blockchainRpcProviderSchema)),
    vmFamilies: screenSection(z.array(blockchainVmFamilySchema)),
    sweepSteps: screenSection(z.array(blockchainSweepCatalogSchema)),
    freshness: freshnessSchema,
  }),
  dependsOn: ['admin.blockchainNetworks'],
  budget: { requests: 1, statements: 40, bytes: 120_000, p95Ms: 800 },
  summary:
    'Blockchain networks admin: every network with its RPC endpoints, the RPC provider catalogue, the enabled VM families and the sweep catalogue of each family, with no credential',
})

export type BlockchainNetworksNetworkRow = z.infer<typeof blockchainNetworkRowSchema>
export type BlockchainNetworksEndpointRow = z.infer<typeof blockchainRpcEndpointRowSchema>
export type BlockchainNetworksProvider = z.infer<typeof blockchainRpcProviderSchema>
export type BlockchainNetworksVmFamily = z.infer<typeof blockchainVmFamilySchema>
export type BlockchainNetworksSweepPhase = z.infer<typeof blockchainSweepPhaseSchema>
export type BlockchainNetworksSweepStep = z.infer<typeof blockchainSweepStepSchema>
export type BlockchainNetworksSweepCatalog = z.infer<typeof blockchainSweepCatalogSchema>
export type AdminBlockchainNetworksQuery = ScreenQuery<typeof adminBlockchainNetworksScreen>
export type AdminBlockchainNetworksData = ScreenData<typeof adminBlockchainNetworksScreen>
